import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { DEFAULT_APP_CONFIG } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader } from '../../components/common';

/**
 * Astrally Pro — subscription products from remote config.
 * Purchases go through Google Play in the Android app; the subscription then
 * applies to this account on web too.
 */
export function ProScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const snap = app.entitlement;
  const pro = snap?.config.pro;
  const base = snap?.config.base;
  const isPro = snap?.tier === 'pro';
  const isNative = Capacitor.isNativePlatform();
  const purchasing = busy !== null;

  /**
   * Only one purchase can be in flight, so the other plan's button has to go
   * quiet — but a plain `disabled` sibling just looks broken. Muted styling
   * plus the banner below says "busy", not "dead".
   */
  const planBtnClass = (productId: string) =>
    `btn btn--primary btn--small${purchasing && busy !== productId ? ' btn--incomplete' : ''}`;

  /**
   * Base-vs-Pro comparison. EVERY number is read from the entitlement
   * snapshot's config — never a literal — so the table can only ever show
   * the limits the server actually enforces. The two Pro fields an older
   * deployed `entitlementCheck` may not send yet fall back to the shared
   * config defaults, which is still config, not a copy in a string.
   */
  const proCards = pro?.discoveryDailyCards ?? DEFAULT_APP_CONFIG.pro.discoveryDailyCards;
  const fairUse = pro?.fairUse ?? DEFAULT_APP_CONFIG.pro.fairUse;

  /** A row with no `pro` cell spans both columns — true of exactly one row,
      because credits deliberately work the same on Base and Pro. */
  const compareRows =
    base && pro
      ? ([
          {
            label: t('pro.compare.price'),
            base: t('pro.compare.priceBase', { price: base.priceDisplay.inr }),
            pro: t('pro.compare.pricePro', {
              monthly: pro.priceDisplay.monthlyInr,
              annual: pro.priceDisplay.annualInr,
            }),
          },
          {
            label: t('pro.compare.ally'),
            base: t('pro.compare.allyBase', { n: base.aiTrialMessagesPerDay }),
            pro: t('pro.compare.allyPro'),
          },
          {
            label: t('pro.compare.discovery'),
            base: t('pro.compare.discoveryBase', { n: base.discoveryDailyCards }),
            pro: t('pro.compare.discoveryPro', { n: proCards }),
          },
          {
            label: t('pro.compare.requests'),
            base: t('pro.compare.requestsBase', { n: base.lifetimeOutgoingRequests }),
            pro: t('pro.compare.requestsPro', { n: pro.dailyOutgoingRequests }),
          },
          {
            label: t('pro.compare.chatMedia'),
            base: t('pro.compare.chatMediaBase'),
            pro: t('pro.compare.chatMediaPro'),
          },
          {
            label: t('pro.compare.feedPhotos'),
            base: t('pro.compare.feedPhotosBase'),
            pro: t('pro.compare.feedPhotosPro'),
          },
          // The one deliberate shared row: credit top-ups are tier-independent.
          { label: t('pro.compare.readings'), base: t('pro.compare.readingsBoth') },
          {
            label: t('pro.compare.introCredits'),
            base: t('pro.compare.introCreditsBase', { n: base.introductoryCredits }),
            pro: t('pro.compare.introCreditsPro'),
          },
        ] as { label: string; base: string; pro?: string }[])
      : null;

  const cellPad = {
    padding: '7px 8px 7px 0',
    verticalAlign: 'top',
    textAlign: 'left',
  } as const;
  const rule = { borderTop: '1px solid var(--border)' } as const;

  const buy = async (productId: string) => {
    setBusy(productId);
    setError('');
    try {
      await app.purchase(productId);
    } catch (e) {
      const raw = String(e);
      setError(
        raw.includes('purchase-in-app-only') || raw.includes('play-billing-pending')
          ? 'Subscribe through Google Play in the Astrally Android app — Pro then applies to this account here too.'
          : raw.slice(0, 200),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('pro.title')} subtitle={t('pro.tagline')} back />

      {isPro && (
        <div className="banner banner--info" style={{ marginBottom: 12 }}>
          {t('pro.active')} · until {snap?.entitlement.proExpiresAtISO?.slice(0, 10)}
        </div>
      )}

      <div className="card card--strong col" style={{ gap: 6, marginBottom: 12 }}>
        {[
          'Fair-use unlimited Astrally AI chat',
          'Full compatibility-ranked discovery + advanced filters',
          // Quotas come from remote config. Rather than promise an invented
          // number, the line waits until the real one is known.
          pro && `${pro.dailyOutgoingRequests} ${t('pro.requests')}`,
          'Complete compatibility breakdowns',
          'AI opening suggestions & compatibility explanations',
          'Priority discovery placement',
          'Longer AI memory and richer personalisation',
        ]
          .filter((f): f is string => Boolean(f))
          .map((f) => (
            <div key={f} className="small">✓ {f}</div>
          ))}
      </div>

      {compareRows && (
        <>
          <div className="small muted" style={{ lineHeight: 1.55, marginBottom: 10 }}>
            {t('pro.compare.intro')}
          </div>
          <div className="card" style={{ marginBottom: 12, overflowX: 'auto' }}>
            <table className="small" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <caption className="section-title" style={{ textAlign: 'left', marginBottom: 6 }}>
                {t('pro.compare.title')}
              </caption>
              <thead>
                <tr>
                  <th style={cellPad} />
                  <th className="tiny muted" scope="col" style={cellPad}>
                    {t('pro.compare.baseCol')}
                  </th>
                  <th className="tiny muted" scope="col" style={cellPad}>
                    {t('pro.compare.proCol')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row) => (
                  <tr key={row.label}>
                    <th className="tiny muted" scope="row" style={{ ...cellPad, ...rule }}>
                      {row.label}
                    </th>
                    {row.pro === undefined ? (
                      <td colSpan={2} style={{ ...cellPad, ...rule }}>
                        {row.base}
                      </td>
                    ) : (
                      <>
                        <td style={{ ...cellPad, ...rule }}>{row.base}</td>
                        <td style={{ ...cellPad, ...rule }}>{row.pro}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
              {t('pro.compare.fairUseNote', {
                perTenMin: fairUse.messagesPerTenMinutes,
                perHour: fairUse.messagesPerHour,
              })}
            </div>
            <div className="tiny muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
              {t('credits.bothTiers')}
            </div>
          </div>
        </>
      )}

      {purchasing && (
        <div className="banner banner--info small row" style={{ marginBottom: 10 }}>
          <span className="spinner" />
          <span>Completing your purchase…</span>
        </div>
      )}

      {error && <div className="banner banner--danger small" style={{ marginBottom: 10 }}>{error}</div>}

      {pro ? (
        <div className="row" style={{ alignItems: 'stretch' }}>
          <div className="card col" style={{ flex: 1, gap: 8 }}>
            <div className="h3">{t('pro.monthly')}</div>
            <div className="h1 gold">{pro.priceDisplay.monthlyInr}</div>
            <button
              className={planBtnClass(pro.products.monthly)}
              disabled={purchasing}
              onClick={() => buy(pro.products.monthly)}
            >
              {busy === pro.products.monthly ? <span className="spinner" /> : t('pro.upgrade')}
            </button>
          </div>
          <div className="card card--strong col" style={{ flex: 1, gap: 8 }}>
            <div className="h3">{t('pro.annual')} <span className="tiny gold">best value</span></div>
            <div className="h1 gold">{pro.priceDisplay.annualInr}</div>
            <button
              className={planBtnClass(pro.products.annual)}
              disabled={purchasing}
              onClick={() => buy(pro.products.annual)}
            >
              {busy === pro.products.annual ? <span className="spinner" /> : t('pro.upgrade')}
            </button>
          </div>
        </div>
      ) : (
        // Prices are remote config. An empty gap where the plans should be
        // reads as a broken screen, so say the prices are still coming.
        <div className="card row">
          <span className="spinner" />
          <span className="small muted">{t('unlock.pricePending')}</span>
        </div>
      )}

      {!isNative && (
        <div className="banner banner--info small" style={{ marginTop: 12, lineHeight: 1.55 }}>
          Subscriptions are purchased through Google Play in the Astrally Android app. Pro then
          applies to this same account on the web automatically.
        </div>
      )}

      <div className="tiny faint center" style={{ marginTop: 14 }}>
        {t('pro.fairUse')}
      </div>
    </div>
  );
}
